import { AsyncPipe, NgIf } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, Component, ChangeDetectionStrategy, Input } from '@angular/core';
import * as i1 from '@taiga-ui/core';
import { TuiButton, TuiDropdown, TuiHint } from '@taiga-ui/core';
import { TUI_EDITOR_OPTIONS, TUI_EDITOR_TOOLBAR_TEXTS } from '@taiga-ui/editor/common';
import { TuiTiptapEditorService } from '@taiga-ui/editor/directives';
import * as i2 from '@taiga-ui/legacy';
import { TuiPaletteModule } from '@taiga-ui/legacy';
import { map, combineLatest, of, distinctUntilChanged } from 'rxjs';
function TuiTableCellColor_div_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 3);
    i0.ɵɵpipe(1, "async");
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵstyleProp("background", i0.ɵɵpipeBind1(1, 2, ctx_r0.color$));
  }
}
function TuiTableCellColor_ng_template_7_Template(rf, ctx) {
  if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "tui-palette", 4);
    i0.ɵɵlistener("selectedColor", function TuiTableCellColor_ng_template_7_Template_tui_palette_selectedColor_0_listener($event) {
      i0.ɵɵrestoreView(_r2);
      const ctx_r0 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r0.setCellColor($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("colors", ctx_r0.colors);
  }
}
let TuiTableCellColor = /*#__PURE__*/(() => {
  class TuiTableCellColor {
    constructor() {
      this.localEditor = null;
      this.options = inject(TUI_EDITOR_OPTIONS);
      this.injectionEditor = inject(TuiTiptapEditorService, {
        optional: true
      });
      this.texts$ = inject(TUI_EDITOR_TOOLBAR_TEXTS);
      this.colorText$ = null;
      this.isActive$ = null;
      this.color$ = null;
      this.colors = this.options.colors;
    }
    set inputEditor(value) {
      this.localEditor = value;
      this.initStream();
    }
    ngOnInit() {
      this.initStream();
    }
    get editor() {
      return this.injectionEditor ?? this.localEditor;
    }
    get icons() {
      return this.options.icons;
    }
    isBlankColor(color) {
      return color === this.options.blankColor;
    }
    setCellColor(color) {
      if (this.editor?.isActive('group')) {
        this.editor.setGroupHilite(color);
      } else if (this.editor?.isActive('table')) {
        this.editor.setCellColor(color);
      }
    }
    initStream() {
      this.colorText$ = this.texts$.pipe(map(texts => this.editor?.isActive('group') && texts.hiliteGroup || this.editor?.isActive('table') && texts.cellColor || ''));
      this.isActive$ = combineLatest([this.editor?.isActive$('table') ?? of(false), this.editor?.isActive$('group') ?? of(false)]).pipe(map(([table, group]) => table || group));
      this.color$ = this.editor?.stateChange$.pipe(map(() => this.editor?.getCellColor() ?? this.editor?.getGroupColor() ?? this.options.blankColor), distinctUntilChanged()) ?? null;
    }
    static {
      this.ɵfac = function TuiTableCellColor_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiTableCellColor)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiTableCellColor,
        selectors: [["tui-table-cell-color"]],
        inputs: {
          colors: "colors",
          inputEditor: [0, "editor", "inputEditor"]
        },
        decls: 9,
        vars: 16,
        consts: [["cellColorDropdown", ""], ["appearance", "icon", "size", "s", "tuiHintDescribe", "", "tuiHintDirection", "top-left", "tuiIconButton", "", "type", "button", 3, "disabled", "iconStart", "tuiDropdown", "tuiDropdownMaxHeight", "tuiDropdownOpen", "tuiHint"], ["class", "t-plate t-plate_paint", 3, "background", 4, "ngIf"], [1, "t-plate", "t-plate_paint"], [1, "t-color-picker", 3, "selectedColor", "colors"]],
        template: function TuiTableCellColor_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "button", 1);
            i0.ɵɵpipe(1, "async");
            i0.ɵɵpipe(2, "async");
            i0.ɵɵtext(3);
            i0.ɵɵpipe(4, "async");
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(5, TuiTableCellColor_div_5_Template, 2, 4, "div", 2);
            i0.ɵɵpipe(6, "async");
            i0.ɵɵtemplate(7, TuiTableCellColor_ng_template_7_Template, 1, 1, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
          }
          if (rf & 2) {
            const cellColorDropdown_r3 = i0.ɵɵreference(8);
            i0.ɵɵproperty("disabled", !i0.ɵɵpipeBind1(1, 8, ctx.isActive$))("iconStart", ctx.icons.paint)("tuiDropdown", cellColorDropdown_r3)("tuiDropdownMaxHeight", 999)("tuiDropdownOpen", false)("tuiHint", i0.ɵɵpipeBind1(2, 10, ctx.colorText$));
            i0.ɵɵadvance(3);
            i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(4, 12, ctx.colorText$), "\n");
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", !ctx.isBlankColor(i0.ɵɵpipeBind1(6, 14, ctx.color$) || ""));
          }
        },
        dependencies: [AsyncPipe, NgIf, TuiButton, i1.TuiDropdownOptionsDirective, i1.TuiDropdownDirective, i1.TuiDropdownOpen, i1.TuiHintDirective, i1.TuiHintDescribe, TuiPaletteModule, i2.TuiPaletteComponent],
        styles: [".t-dropdown[_ngcontent-%COMP%]{padding:.75rem}.t-option[_ngcontent-%COMP%]{transition-property:background;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out}.t-option[_ngcontent-%COMP%]:hover{background:var(--tui-background-neutral-1-hover)}.t-option_margin[_ngcontent-%COMP%]{margin:0 .25rem}.t-plate[_ngcontent-%COMP%]{position:absolute;top:1.9rem;left:.25rem;inline-size:1.5rem;block-size:.25rem;pointer-events:none}.t-plate_paint[_ngcontent-%COMP%]{left:.375rem;inline-size:1.25rem}.t-color-picker[_ngcontent-%COMP%]{box-sizing:border-box;max-inline-size:22.6rem}"],
        changeDetection: 0
      });
    }
  }
  return TuiTableCellColor;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TuiTableCellColor };
